import { HttpMethods } from "../enums/HttpMethods";
import { BaseVariation } from '../interfaces/BaseVariation';
import { DataWrapper } from "../interfaces/DataWrapper";
import { PostVariation } from "../interfaces/PostVariation";
import { RephraseContent } from "../interfaces/RephraseContent";
import { UserVariations } from "../interfaces/UserVariations";
import { Variation } from "../interfaces/Variation";
import { AllVariations } from "../type/AllVariations";
import ContentFeedService from "./contentFeed.service";
import { Language } from "../interfaces/Language";
import { VariationType } from "../enums/VariationType";
import { VariationTipExtended } from "../interfaces/VariationTipExtended";
import { VariationExtended } from "../interfaces/VariationExtended";

const VARIATION_CONTENT_PIECES = 'variation-content-pieces';

class VariationContentPiecesService {

  public static getById(id: number): Promise<Variation> {
    return ContentFeedService.sendRequest<any>(HttpMethods.GET, `${VARIATION_CONTENT_PIECES}/${id}/?populate=*`)
      .then((wrappedVariation: DataWrapper<Variation>) => wrappedVariation.data)
      .catch((error: Error) => {
        throw error;
      });
  }

  public static create(variation: DataWrapper<PostVariation>): Promise<Variation> {
    return ContentFeedService.sendRequest<DataWrapper<PostVariation>>(HttpMethods.POST, VARIATION_CONTENT_PIECES, variation)
      .then((wrappedVariation: DataWrapper<Variation>) => wrappedVariation.data)
      .catch((error: Error) => {
        throw error;
      });
  }

  public static createDraft(variation: DataWrapper<BaseVariation>): Promise<BaseVariation> {
    return ContentFeedService.sendRequest<DataWrapper<BaseVariation>>(HttpMethods.POST, `${VARIATION_CONTENT_PIECES}/make-draft`, variation)
      .then((wrappedDraft: DataWrapper<BaseVariation>) => wrappedDraft.data)
      .catch((error: Error) => {
        throw error;
      });
  }

  public static rephraseContent(content: string): Promise<RephraseContent> {
    const postBody = {
      data: {
        cleared_raw_content: content
      }
    }

    return ContentFeedService.sendRequest<any>(HttpMethods.POST, `${VARIATION_CONTENT_PIECES}/rephrase-content`, postBody)
      .then((data: RephraseContent) => data)
      .catch((error: Error) => {
        throw error;
      });
  }

  public static getByUserEmail(email: string): Promise<AllVariations[]> {
    const postBody = {
      data: {
        user_email: email
      }
    }

    return ContentFeedService.sendRequest<any>(HttpMethods.POST, 'users/get-by-email', postBody)
      .then((wrappedUserVariations: DataWrapper<UserVariations>) => {
        const contentPiecesVariations: VariationExtended[] = wrappedUserVariations.data.variation_content_pieces.map((userVariation) => {
          const variation: VariationExtended = {
            id: userVariation.id,
            attributes: {
              headlines: userVariation.headlines,
              content: userVariation.content,
              site: userVariation.site,
              language: userVariation.language,
              createdAt: userVariation.createdAt,
              updatedAt: userVariation.updatedAt,
              publishedAt: userVariation.publishedAt,
              length: userVariation.length ?? '',
              type: userVariation.type,
              headlinesType: userVariation.headlinesType,
              keepWPBlocks: userVariation.keepWPBlocks,
              structure: userVariation.structure,
              source: VariationType.CONTENT_PIECE,
              user: {
                data: {
                  id: wrappedUserVariations.data.id,
                  attributes: {
                    username: wrappedUserVariations.data.username,
                    email: wrappedUserVariations.data.email
                  }
                }
              }
            }
          };
          return variation;
        });

        const tipsVariations: VariationTipExtended[] = wrappedUserVariations.data.variation_tips.map((variationTip) => {
          const variation: Omit<VariationTipExtended, 'attributes'> & {
            attributes: Omit<VariationTipExtended['attributes'], 'user' | 'tip'> & {
              analysis: string;
              oneLiner: string;
              title: string;
              createdAt: string;
              updatedAt: string;
              publishedAt: string;
              type: string;
              language: DataWrapper<Language>;
            }
          } = {
            id: variationTip.id,
            attributes: {
              analysis: variationTip.analysis,
              oneLiner: variationTip.oneLiner,
              title: variationTip.title,
              createdAt: variationTip.createdAt,
              updatedAt: variationTip.updatedAt,
              publishedAt: variationTip.publishedAt,
              type: variationTip.type,
              source: VariationType.TIP,
              facts: variationTip.facts,
              language: {
                data: {
                  id: variationTip.language.id,
                  attributes: {
                    name: variationTip.language.name,
                    isFilterable: variationTip.language.isFilterable,
                    iso_code: variationTip.language.iso_code
                  }
                }
              }
            }
          };
          
          return variation as VariationTipExtended;
        });

        const allVariations: AllVariations[] = [...contentPiecesVariations, ...tipsVariations];
        
        return allVariations;
      })
      .catch((error: Error) => {
        throw error;
      });
  }

  public static publish(id: number, email: string): Promise<string> {
    const postBody = {
      data: {
        id: id,
        userEmail: email
      }
    }

    return ContentFeedService.sendRequest<{data: {id: number, userEmail: string}}>(HttpMethods.POST, `${VARIATION_CONTENT_PIECES}/publish`, postBody)
      .then((data: {message: string, link: string}) => data.link)
      .catch((error: Error) => {
        throw error;
      });
  }
}

export default VariationContentPiecesService;
